import React from 'react'
import cronstrue from 'cronstrue/i18n'
import {
  SchedulePattern,
  SCHEDULE_OCCURRENCE,
  SCHEDULE_EXPRESSION_TYPE,
  SCHEDULE_RATE_UNIT,
  SCHEDULE_TYPE,
  SCHEDULE_STATUS,
} from '@trustero/trustero-api-web/lib/schedulemanager/schedulemanager_pb'
import { UserRecord } from '@trustero/trustero-api-web/lib/account/account_pb'
import { ScheduleExecution } from '@trustero/trustero-api-web/lib/schedulemanager/schedulemanager_pb'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { formatISODateTime } from 'src/Utils/formatDate'
import { getReceptorName } from './modals/AddReceptorScheduleModal.components'
import {
  ScheduleFrequency,
  DEFAULT_SCHEDULE_TIMEZONE,
} from './modals/ManageScheduleModal.constants'
import {
  ScheduleStatus,
  ScheduleReportData,
} from './SchedulingIndexPage.constants'
import {
  BadgeErrorIcon,
  BadgeSuccessIcon,
  SpinnerStyled,
} from './SchedulingIndexPage.styles'

export interface CronValues {
  frequency: ScheduleFrequency | null
  quantity: number
  hours: number
  minutes: number
  selectedWeek?: number
  selectedDay?: number
  selectedMonth?: number
  monthQuarters?: string
}

export interface StatusDisplay {
  status: string
  icon?: JSX.Element
}

/**
 * Formats a timezone identifier for display.
 * Examples:
 *   "America/New_York" -> "New York"
 *   "America/Los_Angeles" -> "Los Angeles"
 *   "Europe/London" -> "London"
 *   "GMT" -> "GMT"
 */
export const formatTimezoneForDisplay = (timezone: string): string => {
  if (!timezone) return DEFAULT_SCHEDULE_TIMEZONE
  if (timezone === DEFAULT_SCHEDULE_TIMEZONE || timezone === 'UTC')
    return timezone

  // Extract the city/region part after the last slash
  const parts = timezone.split('/')
  const location = parts[parts.length - 1]

  // Replace underscores with spaces
  return location.replace(/_/g, ' ')
}

/**
 * Gets the timezone from a schedule pattern, defaulting to GMT if not set.
 */
export const getScheduleTimezone = (
  pattern: SchedulePattern | undefined,
): string => {
  if (!pattern) return DEFAULT_SCHEDULE_TIMEZONE
  const timezone = pattern.getTimezone()
  return timezone || DEFAULT_SCHEDULE_TIMEZONE
}

export const formatScheduleType = (types: SCHEDULE_TYPE[]): string => {
  if (types.length === 0) return ''
  if (types.length === 1) return formatSingleScheduleType(types[0])
  if (types.length === 2)
    return `${formatSingleScheduleType(
      types[0],
    )} and ${formatSingleScheduleType(types[1])}`
  const allButLast = types.slice(0, -1).map(formatSingleScheduleType).join(', ')
  return `${allButLast}, and ${formatSingleScheduleType(
    types[types.length - 1],
  )}`
}

export const formatSingleScheduleType = (type: SCHEDULE_TYPE): string => {
  switch (type) {
    case SCHEDULE_TYPE.EVIDENCE_COLLECTION:
      return 'Evidence Collection'
    case SCHEDULE_TYPE.PLAYBOOK:
      return 'Playbook'
    case SCHEDULE_TYPE.EXAMINATION_AND_TEST:
      return 'Control Test'
    default:
      return 'Unknown'
  }
}

// Check if cron has a specific time (not an interval like */30 or */2)
const cronHasSpecificTime = (cronExpression: string): boolean => {
  const parts = cronExpression.split(' ')
  // For 5-field cron: minute hour day month dow
  // For 6+ field (Quartz): seconds minute hour day month dow [year]
  const minuteIdx = parts.length >= 6 ? 1 : 0
  const hourIdx = parts.length >= 6 ? 2 : 1

  const minute = parts[minuteIdx]
  const hour = parts[hourIdx]

  // If minute or hour contains * or /, it's an interval pattern
  const isInterval = (field: string) =>
    field.includes('*') || field.includes('/')
  return !isInterval(minute) && !isInterval(hour)
}

export const formatFrequency = (
  pattern: SchedulePattern | undefined,
): string => {
  if (!pattern) return 'N/A'

  const timezone = pattern.getTimezone() || DEFAULT_SCHEDULE_TIMEZONE
  const timezoneDisplay = `${formatTimezoneForDisplay(timezone)} time`

  if (
    pattern.getOccurrence() === SCHEDULE_OCCURRENCE.SCHEDULE_OCCURRENCE_ONE_TIME
  ) {
    return `One Time (${timezoneDisplay})`
  }

  if (
    pattern.getExpressionType() ===
    SCHEDULE_EXPRESSION_TYPE.SCHEDULE_EXPRESSION_TYPE_CRON
  ) {
    const cronExpression = pattern.getCronExpression()
    const expArr = cronExpression.split(' ')
    const showTimezone = cronHasSpecificTime(cronExpression)
    const timezoneSuffix = showTimezone ? ` (${timezoneDisplay})` : ''

    if (expArr.length === 5) {
      return `${cronstrue.toString(cronExpression)}${timezoneSuffix}`
    } else {
      if (expArr.length > 5) {
        return (
          cronstrue.toString(cronExpression.replace(/\/\d+/g, '')) +
          `, every ${expArr[5].split('/').pop()} week.${timezoneSuffix}`
        )
      }
      return `${cronstrue.toString(
        cronExpression.replace(/\/\d+/g, ''),
      )}${timezoneSuffix}`
    }
  }

  if (
    pattern.getExpressionType() ===
    SCHEDULE_EXPRESSION_TYPE.SCHEDULE_EXPRESSION_TYPE_RATE
  ) {
    const value = pattern.getRateValue()
    const unit = pattern.getRateUnit()
    let unitStr = ''
    switch (unit) {
      case SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_DAYS:
        unitStr = value === 1 ? 'Day' : 'Days'
        break
      case SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_HOURS:
        unitStr = value === 1 ? 'Hour' : 'Hours'
        break
      case SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_MINUTES:
        unitStr = value === 1 ? 'Minute' : 'Minutes'
        break
    }
    return `Every ${value} ${unitStr}`
  }

  return 'Unknown'
}

export const extract_cron_values = (cron: string): CronValues => {
  const parts = cron.split(' ')

  const result: CronValues = {
    frequency: null,
    quantity: 0,
    hours: 0,
    minutes: 0,
  }

  if (parts.length < 5) return result

  const [min, hour, dom, month, dow] = parts
  result.minutes = parseInt(min)
  result.hours = parseInt(hour)

  if (min.startsWith('*/')) {
    result.frequency = ScheduleFrequency.THIRTYMINUTES
    return result
  }

  if (hour.startsWith('*/')) {
    result.frequency = ScheduleFrequency.HOURLY // * */h */d * *
    result.quantity = parseInt(hour.substring(2))
    return result
  }

  if (dom.startsWith('*/')) {
    const interval = parseInt(dom.substring(2))
    if (dow === '*') {
      result.frequency = ScheduleFrequency.DAY
      result.quantity = interval
    } else {
      result.frequency = ScheduleFrequency.WEEK // m h d */M *
      result.quantity = interval / 7
      result.selectedWeek = parseInt(dow)
    }
    return result
  }

  if (!dom.includes('*')) {
    result.selectedDay = parseInt(dom)

    if (month.startsWith('*/')) {
      result.frequency = ScheduleFrequency.MONTH // m h d */M *
      result.quantity = parseInt(month.substring(2))
    } else {
      const months = month.split(',').map(Number)
      if (months.length === 1) {
        result.frequency = ScheduleFrequency.ANNUALLY // m h d M *
        result.quantity = 1
        result.selectedMonth = months[0]
      } else {
        result.frequency = ScheduleFrequency.QUARTERLY // m h d 1,4,7,10 *
        result.quantity = 1
        result.monthQuarters = month
      }
    }
  } else if (dow !== '*') {
    result.frequency = ScheduleFrequency.WEEK
    result.quantity = 1
    result.selectedWeek = parseInt(dow)
  }

  return result
}

export const count_users_get_notified = (
  parameters: string,
  users: UserRecord[],
): number => {
  if (!parameters) return 0
  const usersSet = new Set(users.map((user) => user.getId()))
  const params = JSON.parse(parameters)
  const userIds: string[] = params?.notifications?.users || []
  return userIds.filter((userId) => usersSet.has(userId)).length
}

export const get_status_display = (status: ScheduleStatus): StatusDisplay => {
  switch (status) {
    case ScheduleStatus.Completed:
      return { status: 'Success', icon: <BadgeSuccessIcon /> } as StatusDisplay
    case ScheduleStatus.Failed:
      return { status: 'Failed', icon: <BadgeErrorIcon /> } as StatusDisplay
    case ScheduleStatus.Running:
      return {
        status: 'Running',
        icon: <SpinnerStyled size="xs" color="primary" />,
      } as StatusDisplay
    default:
      return { status: '' } as StatusDisplay
  }
}

export const get_status = (status: SCHEDULE_STATUS): ScheduleStatus => {
  switch (status) {
    case SCHEDULE_STATUS.SCHEDULE_COMPLETED:
      return ScheduleStatus.Completed
    case SCHEDULE_STATUS.SCHEDULE_ERROR:
      return ScheduleStatus.Failed
    case SCHEDULE_STATUS.SCHEDULE_RUNNING:
      return ScheduleStatus.Running
    default:
      return ScheduleStatus.Default
  }
}

export const doesScheduleTypeInclude = (
  recordTypes: SCHEDULE_TYPE[],
  filterType: SCHEDULE_TYPE,
): boolean => {
  if (recordTypes.includes(filterType)) return true

  return false
}

export const formatExecutionTime = (timestamp: number | undefined): string => {
  if (!timestamp) return '-'
  return formatISODateTime(timestamp * 1000)
}

export const getExecutionDetails = (
  executions: ScheduleExecution[],
): ScheduleReportData[] => {
  const details: ScheduleReportData[] = []

  executions.forEach((execution, index) => {
    const evidenceCollection = execution.getEvidenceCollection()
    if (evidenceCollection) {
      const metadata = evidenceCollection.getMetadata()
      if (metadata) {
        details.push({
          Id: `ec-${index}`,
          ReportId: '',
          ReportName: 'Evidence Collection',
          StartTime: formatExecutionTime(metadata.getStartTime()),
          EndTime: formatExecutionTime(metadata.getEndTime()),
          Summary: metadata.getSummary(),
        })
      }
    }

    const examineAndTest = execution.getExamineAndTest()
    if (examineAndTest) {
      const metadata = examineAndTest.getMetadata()
      if (metadata) {
        details.push({
          Id: `et-${index}`,
          ReportId: '',
          ReportName: 'Control Test',
          StartTime: formatExecutionTime(metadata.getStartTime()),
          EndTime: formatExecutionTime(metadata.getEndTime()),
          Summary: metadata.getSummary(),
          AuditRunId: examineAndTest.getAuditRunId(),
        })
      }
    }

    const playbook = execution.getPlaybook()
    if (playbook) {
      const metadata = playbook.getMetadata()
      if (metadata) {
        details.push({
          Id: `pb-${index}`,
          ReportId: playbook.getReportArtifactId(),
          ReportName: 'Playbook',
          StartTime: formatExecutionTime(metadata.getStartTime()),
          EndTime: formatExecutionTime(metadata.getEndTime()),
          Summary: metadata.getSummary(),
          ChatId: playbook.getChatId(),
        })
      }
    }
  })

  return details
}

export const formatEvidenceCollectionSelection = (
  parameters: string,
  allReceptors: ReceptorRecord[],
): React.ReactNode => {
  if (!parameters) return 'None'
  let params
  try {
    params = JSON.parse(parameters)
  } catch (e) {
    return 'None'
  }

  const evidenceCollection = params?.evidenceCollection
  if (!evidenceCollection) return 'None'

  const types: string[] = []
  const receptors: string[] = []

  if (evidenceCollection.allEvidenceReceptors) {
    types.push('All evidence receptors')
  }
  if (evidenceCollection.allGRCReceptors) {
    types.push('All GRC receptors')
  }
  if (evidenceCollection.allManagementReceptors) {
    types.push('All document management receptors')
  }
  if (evidenceCollection.allWorkflowReceptors) {
    types.push('All workflow receptors')
  }

  const selectedReceptorIds: string[] = evidenceCollection.receptors || []
  if (selectedReceptorIds.length > 0) {
    const receptorMap = new Map<string, ReceptorRecord>()
    allReceptors.forEach((r) => receptorMap.set(r.getId(), r))

    selectedReceptorIds.forEach((id) => {
      const receptor = receptorMap.get(id)
      if (receptor) {
        let name = ''
        try {
          name = getReceptorName(receptor.getModelid())
        } catch (e) {
          name = receptor.getModelid()
        }
        receptors.push(`${name} - ${receptor.getTenantid()}`)
      } else {
        receptors.push(`Unknown Receptor (${id})`)
      }
    })
  }

  if (types.length === 0 && receptors.length === 0) return 'None'

  return (
    <div>
      {types.length > 0 && <div>By Type: {types.join(', ')}</div>}
      {receptors.length > 0 && <div>By Receptor: {receptors.join(', ')}</div>}
    </div>
  )
}

export const formatPlaybookSelection = (parameters: string): string | null => {
  if (!parameters) return null
  let params
  try {
    params = JSON.parse(parameters)
  } catch (e) {
    return null
  }

  const playbook = params?.playbook
  if (!playbook?.playbookId) return null

  return playbook.playbookId
}

export const formatExamineAndTestSelection = (
  parameters: string,
  allFrameworks: ComplianceFramework[],
  allControls: Control[],
): React.ReactNode => {
  if (!parameters) return 'None'
  let params
  try {
    params = JSON.parse(parameters)
  } catch (e) {
    return 'None'
  }

  const examineAndTest = params?.examineAndTest
  if (!examineAndTest) return 'None'

  const types: string[] = []
  const frameworks: string[] = []
  const controls: string[] = []

  if (examineAndTest.allControls) {
    types.push('All controls')
  }

  if (
    examineAndTest.controlFrameworks &&
    examineAndTest.controlFrameworks.length > 0
  ) {
    const frameworkMap = new Map<string, string>()
    allFrameworks.forEach((f) => frameworkMap.set(f.getModelId(), f.getName()))
    examineAndTest.controlFrameworks.forEach((id: string) => {
      frameworks.push(frameworkMap.get(id) || id)
    })
  }

  if (examineAndTest.controls && examineAndTest.controls.length > 0) {
    const controlMap = new Map<string, string>()

    allControls.forEach((c) => {
      const label = `${c.getModelId()} ${c.getName()}`
      controlMap.set(c.getId(), label)
      const newId = c.getNewControlId()
      if (newId && !controlMap.has(newId)) {
        controlMap.set(newId, label)
      }
    })

    examineAndTest.controls.forEach((id: string) => {
      controls.push(controlMap.get(id) || id)
    })
  }

  if (types.length === 0 && frameworks.length === 0 && controls.length === 0)
    return 'None'

  return (
    <div>
      {types.length > 0 && <div>{types.join(', ')}</div>}
      {frameworks.length > 0 && (
        <div>By Framework: {frameworks.join(', ')}</div>
      )}
      {controls.length > 0 && <div>By Control: {controls.join(', ')}</div>}
    </div>
  )
}
